import React from 'react';
import PropTypes from 'prop-types';
import styled, { ThemeProvider } from 'styled-components';
import DataService from 'lib/DataService';

import Icon from 'components/Icon';

const Api = new DataService();

const theme = {
	main: '#4A90E2',
	lightGrey: '#D6D6D6',
	brand: 'rgb(51, 51, 51)',
	heading: 'rgb(77, 77, 77)',
	danger: '#D0021B',
	mainOpacity: percentage => `rgba(77, 77, 77, ${percentage/100})`,
};

const StyledInputGroup = styled.div`
  margin: 10px 0 30px;
`;
const StyledLabel = styled.label`
  font-size: 16px;
  color: #556582;
  display: block;
`;
const StyledInput = styled.input`
  background-color: rgb(240,240,240);
	border: 2px solid rgb(240,240,240);
	border-radius: 4px;
  width: 100%;
  height: 50px;
	padding: 0 8px;
	margin-top: 8px;
  font-size: 18px;
	transition: all 0.3s ease-out;

	&:hover {
		background-color: rgb(230,230,230);
	}

  &:focus {
		border-color: #6990D9;
		background-color: white;
		outline: none;
  }
`;

const StyledInputWithLabel = ({ label, ...rest }) => (
	<StyledInputGroup>
		<StyledLabel>
			{label}
			<StyledInput {...rest} />
		</StyledLabel>
	</StyledInputGroup>
);

StyledInputWithLabel.propTypes = {
	label: PropTypes.string,
}

const Select = styled.select`
  appearance: none;
  color: rgb(60,60,60);
  font-size: 14px;
  border: 0px;
  background-color: rgba(0,0,0,0);
  height: 24px;
  cursor: pointer;
	width: 100%;
	padding-right: 32px;
`;

const SelectContainer = styled.label`
	display: inline-block;
  position: relative;
  height: 32px;
	cursor: pointer;
	padding: 4px 8px;
	background-color: rgb(240,240,240,1);
`;

const SelectIcon = styled(Icon)`
	position: absolute;
	top: calc(50% - 10px);
	right: 8px;
	width: 20px;
	height: 20px;
	pointer-events: none;
`;
/*
<StyledSelect
	id={id}
	options={[
		{ value, name }
	]}
	value={value}
/>
*/
const StyledSelect = ({ id, options, value, style, ...rest }) => {
	return (
		<SelectContainer htmlFor={id} style={style}>
			<Select id={id} value={value} {...rest}>
				{options.map(option => {
					if (option.options) { // OPTGROUP
						return (
							<optgroup key={option.name} label={option.name}>
								{option.options.map(option => (
									<option
										key={option.value}
										value={option.value}
										selected={option.selected}
									>{option.name}</option>
								))}
							</optgroup>
						)
					}

					return (
						<option
							key={option.value}
							value={option.value}
							selected={option.selected}
						>{option.name}</option>
					)
				})}
			</Select>
			<SelectIcon icon="cheveron-down" />
		</SelectContainer>
	)
}

StyledSelect.propTypes = {
	id: PropTypes.string,
	value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	options: PropTypes.arrayOf(PropTypes.shape({
		value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		name: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	})),
	style: PropTypes.object,
}

const BigBlueButton = styled.button`
  color: white;
  padding: 16px 32px;
  margin: 0;
  border: 0px;
  border-radius: 4px;
  text-align: center;
  display: block;
  justify-content: center;
  background-color: ${theme.main};
  font-size: 18px;
  font-weight: 200;
  cursor: pointer;
  transition: background-color 0.2s ease-out, box-shadow 0.2s ease-out, transform 0.2s ease-out;
  outline: none;
  user-select: none;

  &:hover,
  &:focus {
    background: ${theme.main};
  }

  &:active {
		background: ${theme.main};
	}

	&[disabled] {
		background-color: rgba(230,230,230);
	}
`;

const GhostButton = styled.button`
	border: 1px solid rgb(51, 51, 51);
	background-color: white;
	border-radius: 4px;
	padding: 8px 16px;
	text-align: center;
	font-size: 14px;
	color: rgb(77, 77, 77);
	cursor: pointer;
	transition: all 0.3s ease-out;
`;

const AccordionContainer = styled.div`
	border-bottom: 1px solid #F2F2F2;
`;
const AccordionTitle = styled.label`
	display: block;
	padding: 16px 40px;
	color: ${props => props.open ? props.theme.main : '#787878'};
	font-size: 14px;
	position: relative;
	cursor: pointer;

	&:after {
		content: "";
		position: absolute;
		top: 50%;
		right 40px;
		width: 0px;
		height: 0px;
		border: 5px solid rgba(0,0,0,0);
		border-${props => props.open ? 'bottom' : 'top'}-color: ${props => props.open ? props.theme.main : '#787878'};
		transform: translateY(${props => props.open ? '-5px' : '0px'});
	}
`;
const AccordionBody = styled.div`
	padding: 0px 40px 40px;
	display: ${props => props.open ? 'block' : 'none'};
`;

class Accordion extends React.Component {
	state = {
	  open: this.props.open || false,
	}

	toggleOpenHandler = () => this.setState(state => ({ open: !state.open}));

	render() {
	  return (
	    <ThemeProvider theme={theme}>
	      <AccordionContainer>
	        <AccordionTitle
	          open={this.state.open}
	          onClick={() => this.toggleOpenHandler()}
	        >{this.props.title}</AccordionTitle>
	        <AccordionBody open={this.state.open}>{this.props.children}</AccordionBody>
	      </AccordionContainer>
	    </ThemeProvider>
	  )
	}
}

Accordion.propTypes = {
	title: PropTypes.string,
	open: PropTypes.bool,
	children: PropTypes.node,
}

const InfoContainer = styled.div`
	margin: 40px 0;
	display: flex;
	align-items: center;
`;

const InfoIcon = styled(Icon)`
	margin-right: 8px;

	svg {
		fill: ${props => props.fill || '#9B9B9B'};
	}
`;

const InfoText = styled.div`
	p {
		color: #9B9B9B;
		font-size: 14px;

		&:nth-last-child(1n+2) {
			margin-bottom: 0.5em;
		}
	}
`;

const Info = ({ fill, icon, info, additionalContent }) => {
	return (
		<InfoContainer>
			<InfoIcon className="icon" fill={fill} icon={icon} />
			<InfoText>{info.split('\n').map(paragraph => <p key={paragraph}>{paragraph}</p>)}{additionalContent}</InfoText>
		</InfoContainer>
	)
}

Info.propTypes = {
	icon: PropTypes.string,
	fill: PropTypes.string,
	info: PropTypes.string,
	additionalContent: PropTypes.element,
}

const generateId = () => Api.generateUuid();

export {
	StyledInputGroup,
	StyledInput,
	StyledLabel,
	StyledInputWithLabel,
	StyledSelect,
	BigBlueButton,
	GhostButton,
	Accordion,
	Info,
	theme,
	generateId,
}
